import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { PairedRunner } from '../../services/runner-pairing.service';
import { AvatarComponent } from '../avatar/avatar.component';
import { RunnerState, runnerState } from '../runner-list/runner-list.component';
import { VerwaltungStore } from '../verwaltung/verwaltung.store';
import { VwKopfComponent } from '../verwaltung/vw-kopf.component';

interface RechnerZeile {
  id: string;
  name: string;
  etiketten: string;
  zustand: RunnerState;
  zuletzt: string | null;
  werkzeug: string;
  einrichter: string | null;
}

@Component({
  selector: 'app-vw-rechner',
  standalone: true,
  imports: [AvatarComponent, MatButtonModule, RouterLink, TranslatePipe, VwKopfComponent],
  templateUrl: './vw-rechner.component.html',
  styleUrl: './vw-rechner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VwRechnerComponent {
  readonly store = inject(VerwaltungStore);
  private readonly translate = inject(TranslateService);

  private readonly sprache = toSignal(this.translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this.translate.currentLang,
  });

  readonly zeilen = computed<RechnerZeile[]>(() => {
    this.sprache();
    const datumsformat = this.translate.instant('verwaltung.rechner.datumsformat');
    return (this.store.rechner() ?? [])
      .filter((r) => r.connected)
      .map((r) => {
        const zustand = runnerState(r);
        return {
          id: r.id,
          name: r.name,
          etiketten: etikettenVon(r),
          zustand,
          // Das Muster kommt je Sprache aus i18n; en-US ist immer registriert und liefert die englischen Monatsnamen.
          zuletzt: zustand === 'offline' ? formatDate(r.lastSeenAt, datumsformat, 'en-US') : null,
          werkzeug: this.werkzeugVon(r.engine),
          einrichter: r.setUpBy?.displayName ?? null,
        };
      });
  });

  private werkzeugVon(engine: string): string {
    const schluessel = `runners.engine.${engine}`;
    const text = this.translate.instant(schluessel);
    return text === schluessel ? engine : text;
  }
}

function etikettenVon(r: PairedRunner): string {
  return [...new Set([...r.reportedLabels, ...r.managedLabels])].join(', ');
}
